import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  User,
  Building2,
  MapPin,
  Phone,
  Mail,
  Search,
  Laptop,
  ArrowRight,
  Sparkles,
  Loader2,
  CheckCircle2,
  Clock,
  ShieldCheck,
} from "lucide-react";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import trustMarkBadge from "@/assets/Trusted.webp";
import dpoDpsMark from "@/assets/dpo-dps-mark.webp";
import { validateDemoReferral } from "@/api/email";
import { useDemoRequest } from "@/hooks/email/useDemoRequest";
import TurnstileWidget from "@/components/shared/TurnstileWidget";
import TimePicker from "@/components/shared/TimePicker";
import { toast } from "sonner";
import { z } from "zod";

/**
 * Demo request modal with multi-step validation and Turnstile captcha.
 */

// --- Advanced Framer Motion Variants ---
const modalReveal = {
  hidden: {
    opacity: 0,
    scale: 0.98,
  },
  show: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: "easeOut",
      delayChildren: 0.1,
      // Increased stagger time from 0.35 to 0.45.
      // This forces the right panel to wait a little longer,
      // letting the left panel's vertical slide be the star of the show first.
      staggerChildren: 0.45,
    },
  },
};

const leftPanelReveal = {
  // Increased 'y' distance from 150 to 400 for a much larger, more visible travel distance
  hidden: { opacity: 0, y: 400, filter: "blur(10px)" },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    // Lowered stiffness and damping for a slightly slower, more sweeping entrance
    transition: { type: "spring", stiffness: 120, damping: 20 },
  },
};

const rightPanelReveal = {
  // Increased 'x' distance so it matches the dramatic feel of the left panel
  hidden: { opacity: 0, x: 250, filter: "blur(10px)" },
  show: {
    opacity: 1,
    x: 0,
    filter: "blur(0px)",
    transition: { type: "spring", stiffness: 150, damping: 22 },
  },
};

const formContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.15 },
  },
};

const formItem = {
  hidden: { opacity: 0, x: 20, y: 10, filter: "blur(4px)" },
  show: {
    opacity: 1,
    x: 0,
    y: 0,
    filter: "blur(0px)",
    transition: { type: "spring", stiffness: 280, damping: 22 },
  },
};

const MIN_PREFERRED_TIME = "10:00";
const MAX_PREFERRED_TIME = "20:00";
const MIN_PREFERRED_TIME_DISPLAY = "10:00 AM";
const MAX_PREFERRED_TIME_DISPLAY = "08:00 PM";
const SCHEDULE_TIME_ZONE = "Asia/Manila";
const SLOT_STEP_MINUTES = 15;
const MAX_BRANCHES = 20;
const REFERRAL_QUERY_KEYS = ["vetassist", "referral", "ref"];

const toMinutesFrom24Hour = (timeStr) => {
  const [hour, minute] = String(timeStr).split(":").map(Number);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
  return hour * 60 + minute;
};

const roundUpMinutes = (minutes, step) => Math.ceil(minutes / step) * step;

const getManilaNowInfo = () => {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: SCHEDULE_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const parts = formatter.formatToParts(new Date());
  const byType = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return {
    dateKey: `${byType.year}-${byType.month}-${byType.day}`,
    minutes: Number(byType.hour) * 60 + Number(byType.minute),
  };
};

const toDisplayTimeFromMinutes = (minutes) => {
  const hour24 = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const period = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 || 12;
  return `${String(hour12).padStart(2, "0")}:${String(minute).padStart(2, "0")} ${period}`;
};

const getDynamicMinAllowedMinutes = (preferredDate) => {
  const baseMinMinutes = toMinutesFrom24Hour(MIN_PREFERRED_TIME) ?? 0;
  if (!preferredDate) return baseMinMinutes;

  const manilaNow = getManilaNowInfo();
  if (preferredDate !== manilaNow.dateKey) return baseMinMinutes;

  return Math.max(baseMinMinutes, roundUpMinutes(manilaNow.minutes + 60, SLOT_STEP_MINUTES));
};

const getDynamicMinAllowedDisplay = (preferredDate) =>
  toDisplayTimeFromMinutes(getDynamicMinAllowedMinutes(preferredDate));

const to24HourTime = (timeStr) => {
  if (!timeStr) return "";

  const already24Hour = timeStr.match(/^(\d{2}):(\d{2})$/);
  if (already24Hour) return timeStr;

  const matched = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!matched) return "";

  let [, hour, minute, period] = matched;
  let hourNumber = Number(hour);

  if (period.toUpperCase() === "AM") {
    if (hourNumber === 12) hourNumber = 0;
  } else if (hourNumber !== 12) {
    hourNumber += 12;
  }

  return `${String(hourNumber).padStart(2, "0")}:${minute}`;
};

const isTimeWithinRange = (timeStr, preferredDate) => {
  const converted = to24HourTime(timeStr);
  if (!converted) return false;

  const minutes = toMinutesFrom24Hour(converted);
  const minMinutes = getDynamicMinAllowedMinutes(preferredDate);
  const maxMinutes = toMinutesFrom24Hour(MAX_PREFERRED_TIME);

  if (minutes === null || maxMinutes === null) return false;
  return minutes >= minMinutes && minutes <= maxMinutes;
};

const isValidEmail = (email) => {
  const normalized = email.trim();
  if (!normalized) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized);
};

const isValidPhoneNumber = (phone) => /^\+?\d{10,13}$/.test(phone.trim());
const sanitizeLettersOnly = (value) => value.replace(/[^A-Za-z\s]/g, "");

const getPreferredTimeSchema = (preferredDate) =>
  z
    .string()
    .min(1, "Please select your preferred demo time.")
    .refine(
      (value) => isTimeWithinRange(value, preferredDate),
      `Preferred time must be between ${getDynamicMinAllowedDisplay(preferredDate)} and ${MAX_PREFERRED_TIME_DISPLAY} (PH/Manila time).`
    );

export default function DemoModal({ trigger, open, onOpenChange, inline = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [timeValidationError, setTimeValidationError] = useState("");
  const [referralStatus, setReferralStatus] = useState({
    isChecking: false,
    isValid: false,
    clinicName: "",
    message: "",
  });
  
  const [fieldErrors, setFieldErrors] = useState({});
  const fieldRefs = useRef({});

  const setFieldRef = (fieldName) => (node) => {
    if (node) fieldRefs.current[fieldName] = node;
  };

  const clearFieldError = (...fieldNames) => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      fieldNames.forEach((fieldName) => delete next[fieldName]);
      return next;
    });
  };

  const scrollToFirstError = (errors) => {
    const firstField = Object.keys(errors)[0];
    if (!firstField) return;

    requestAnimationFrame(() => {
      const element = fieldRefs.current[firstField];
      if (!element) return;

      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      const focusTarget = element.querySelector(
        "input, button, [role='combobox'], [tabindex]:not([tabindex='-1'])"
      );

      focusTarget?.focus?.({ preventScroll: true });
    });
  };

  const errorClass = (fieldName) =>
    fieldErrors[fieldName]
      ? " !border-rose-500 !bg-rose-50/70 focus:!border-rose-500 focus:!ring-rose-500/10 focus-visible:!ring-rose-500/10"
      : "";

  const errorText = (fieldName) =>
    fieldErrors[fieldName] ? (
      <p className="mt-2 text-xs font-medium text-rose-500">
        {fieldErrors[fieldName]}
      </p>
    ) : null;

  const isTurnstileEnabled = Boolean(import.meta.env.VITE_TURNSTILE_SITE_KEY);
  const turnstileRef = useRef(null);
  const [formData, setFormData] = useState({
    hasSoftware: "",
    currentSoftwareName: "",
    firstName: "",
    lastName: "",
    email: "",
    clinicContactNumber: "",
    veterinarianContactNumber: "",
    clinicName: "",
    clinicAddressLine1: "",
    clinicAddressLine2: "",
    businessType: "Veterinary Clinic",
    preferredDate: "",
    preferredTime: "",
    numberOfBranches: 1,
    clinicBranches: [],
    referralCode: "",
    referralName: "",
  });
    const startOfToday = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate()
  );

  const currentYear = startOfToday.getFullYear();
  const calendarStartMonth = new Date(currentYear, 0, 1);
  const calendarEndMonth = new Date(currentYear + 20, 11, 1);

  const { mutate: submitDemoRequest, isPending } = useDemoRequest();
  const isVisible = inline ? true : Boolean(open);

  useEffect(() => {
    if (inline) return undefined;

    if (open) {
      document.body.classList.add("demo-modal-open");
    } else {
      document.body.classList.remove("demo-modal-open");
    }
    return () => document.body.classList.remove("demo-modal-open");
  }, [open, inline]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const referralCode = REFERRAL_QUERY_KEYS.map((key) => params.get(key))
      .find(Boolean)
      ?.trim();

    if (!referralCode) return;

    setFormData((prev) => ({
      ...prev,
      referralCode,
      referralName: prev.referralName || referralCode,
    }));
  }, [location.search]);

  useEffect(() => {
    if (!formData.referralCode) {
      setReferralStatus({
        isChecking: false,
        isValid: false,
        clinicName: "",
        message: "",
      });
      return undefined;
    }

    let isCurrent = true;

    setReferralStatus({
      isChecking: true,
      isValid: false,
      clinicName: "",
      message: "Checking referral...",
    });

    validateDemoReferral(formData.referralCode)
      .then((referral) => {
        if (!isCurrent) return;

        if (referral?.isValid) {
          setFormData((prev) => ({
            ...prev,
            referralName: referral.clinicName || prev.referralName,
          }));
          setReferralStatus({
            isChecking: false,
            isValid: true,
            clinicName: referral.clinicName || formData.referralCode,
            message: `Referred by ${referral.clinicName || formData.referralCode}`,
          });
          return;
        }

        setReferralStatus({
          isChecking: false,
          isValid: false,
          clinicName: "",
          message: "Referral clinic was not found in onboarded Vet Assist clients.",
        });
      })
      .catch(() => {
        if (!isCurrent) return;

        setReferralStatus({
          isChecking: false,
          isValid: false,
          clinicName: "",
          message: "Unable to verify referral right now.",
        });
      });

    return () => {
      isCurrent = false;
    };
  }, [formData.referralCode]);

  const sanitizePhoneInput = (value) => {
    if (value.startsWith("+")) {
      return "+" + value.slice(1).replace(/\D/g, "").slice(0, 12);
    }
    return value.replace(/\D/g, "").slice(0, 11);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    clearFieldError(name);

    if (["clinicContactNumber", "veterinarianContactNumber"].includes(name)) {
      setFormData((prev) => ({ ...prev, [name]: sanitizePhoneInput(value) }));
    } else if (["firstName", "lastName"].includes(name)) {
      setFormData((prev) => ({ ...prev, [name]: sanitizeLettersOnly(value) }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const createEmptyClinicBranch = () => ({
    clinicName: "",
    clinicAddressLine1: "",
    clinicAddressLine2: "",
    clinicContactNumber: "",
    veterinarianContactNumber: "",
  });

  const handleNumberOfBranchesChange = (value) => {
    clearFieldError("numberOfBranches");

    const nextCount = Math.min(
      MAX_BRANCHES,
      Math.max(1, Number(value || 1))
    );

    setFormData((prev) => ({
      ...prev,
      numberOfBranches: nextCount,
      clinicBranches: (prev.clinicBranches || []).slice(0, nextCount),
    }));
  };

  const handleAddClinicBranch = () => {
    setFormData((prev) => {
      const maxBranches = Number(prev.numberOfBranches || 0);
      const currentBranches = prev.clinicBranches || [];

      if (maxBranches > 0 && currentBranches.length >= maxBranches) {
        toast.error("You cannot add more branch details than the number of branches.");
        return prev;
      }

      return {
        ...prev,
        clinicBranches: [...currentBranches, createEmptyClinicBranch()],
      };
    });
  };

  const handleRemoveClinicBranch = (index) => {
    setFormData((prev) => ({
      ...prev,
      clinicBranches: (prev.clinicBranches || []).filter((_, i) => i !== index),
    }));
  };

  const handleClinicBranchChange = (index, field, value) => {
    clearFieldError(`clinicBranches.${index}.${field}`);

    setFormData((prev) => {
      const nextBranches = [...(prev.clinicBranches || [])];

      nextBranches[index] = {
        ...nextBranches[index],
        [field]: field.includes("Number") ? sanitizePhoneInput(value) : value,
      };

      return {
        ...prev,
        clinicBranches: nextBranches,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = getStepValidationErrors(4);

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setTimeValidationError(errors.preferredTime || "");
      scrollToFirstError(errors);
      return;
    }

    const timeValidation = getPreferredTimeSchema(formData.preferredDate).safeParse(
      formData.preferredTime
    );
    if (!timeValidation.success) {
      setTimeValidationError(timeValidation.error.issues[0]?.message || "Invalid time selected.");
      return;
    }

    setTimeValidationError("");

    if (isTurnstileEnabled && !turnstileToken) {
      toast.error("Captcha verification failed. Please try again.");
      return;
    }

    if (formData.referralCode && !referralStatus.isValid) {
      toast.error("Referral must be an onboarded Vet Assist clinic.");
      return;
    }

    submitDemoRequest(
      { ...formData, preferredTime: to24HourTime(formData.preferredTime), turnstileToken },
      {
        onSuccess: () => {
          const thankYouState = {
            firstName: formData.firstName,
            preferredDate: formData.preferredDate,
            preferredTime: to24HourTime(formData.preferredTime),
            referralName: formData.referralName,
          };

          if (!inline && onOpenChange) onOpenChange(false);
          setTurnstileToken("");
          setTurnstileResetKey((prev) => prev + 1);
          turnstileRef.current?.reset?.();
          navigate("/demo/thank-you", {
            state: thankYouState,
            replace: inline,
          });
        },
      }
    );
  };

  const handleClearForm = () => {
    setFormData({
      hasSoftware: "",
      currentSoftwareName: "",
      firstName: "",
      lastName: "",
      email: "",
      clinicContactNumber: "",
      veterinarianContactNumber: "",
      clinicName: "",
      clinicAddressLine1: "",
      clinicAddressLine2: "",
      businessType: "Veterinary Clinic",
      preferredDate: "",
      preferredTime: "",
      numberOfBranches: 1,
      clinicBranches: [],
      referralCode: "",
      referralName: "",
    });
        setCurrentStep(1);
        setTurnstileToken("");
        setTimeValidationError("");
        setTurnstileResetKey((prev) => prev + 1);
  };

  useEffect(() => {
    if (!isVisible) return;
    setCurrentStep(1);
    setTurnstileToken("");
    setTimeValidationError("");
    setTurnstileResetKey((prev) => prev + 1);
  }, [isVisible]);

  const handlePreferredTimeChange = (newTime) => {
    clearFieldError("preferredTime");

    setFormData((prev) => ({ ...prev, preferredTime: newTime }));

    const timeValidation = getPreferredTimeSchema(formData.preferredDate).safeParse(newTime);
    if (!timeValidation.success) {
      setTimeValidationError(timeValidation.error.issues[0]?.message || "Invalid time selected.");
      return;
    }

    setTimeValidationError("");
  };

  const handlePreferredDateChange = (newDate) => {
    clearFieldError("preferredDate", "preferredTime");

    setFormData((prev) => {
      const nextState = {
        ...prev,
        preferredDate: newDate,
      };

      if (prev.preferredTime && !isTimeWithinRange(prev.preferredTime, newDate)) {
        nextState.preferredTime = "";
      }

      return nextState;
    });

    setTimeValidationError("");
  };

  const isSoftwareComplete =
    formData.hasSoftware === "no" ||
    (formData.hasSoftware === "yes" && formData.currentSoftwareName.trim() !== "");
  const hasValidEmail = isValidEmail(formData.email);
  const hasValidClinicContactNumber = isValidPhoneNumber(formData.clinicContactNumber);
  const isPersonalInfoComplete =
    formData.firstName.trim() !== "" &&
    formData.lastName.trim() !== "" &&
    formData.clinicContactNumber.trim() !== "" &&
    hasValidClinicContactNumber &&
    hasValidEmail;
    
  const hasValidProvidedBranchDetails = (formData.clinicBranches || []).every((branch) => {
    const hasAnyValue =
      branch.clinicName?.trim() ||
      branch.clinicAddressLine1?.trim() ||
      branch.clinicAddressLine2?.trim() ||
      branch.clinicContactNumber?.trim() ||
      branch.veterinarianContactNumber?.trim();

    if (!hasAnyValue) return true;

    return (
      branch.clinicName?.trim() &&
      branch.clinicAddressLine1?.trim() &&
      branch.clinicContactNumber?.trim() &&
      isValidPhoneNumber(branch.clinicContactNumber)
    );
  });

  const isPrimaryClinicComplete =
    formData.clinicName.trim() !== "" &&
    formData.clinicAddressLine1.trim() !== "";

  const isClinicInfoComplete =
    isPrimaryClinicComplete &&
    Number(formData.numberOfBranches || 0) >= 1 &&
    Number(formData.numberOfBranches || 0) <= MAX_BRANCHES &&
    (formData.clinicBranches || []).length <= Number(formData.numberOfBranches || 0) &&
    hasValidProvidedBranchDetails;

  const isScheduleComplete =
    formData.preferredDate !== "" &&
    isTimeWithinRange(formData.preferredTime, formData.preferredDate);

  const canProceedByStep = {
    1: isSoftwareComplete,
    2: isPersonalInfoComplete,
    3: isClinicInfoComplete,
    4: isScheduleComplete,
  };

  const getStepValidationErrors = (step) => {
    const errors = {};

    if (step === 1) {
      if (!formData.hasSoftware) {
        errors.hasSoftware = "Please select your current software status.";
      }

      if (
        formData.hasSoftware === "yes" &&
        !formData.currentSoftwareName.trim()
      ) {
        errors.currentSoftwareName = "Please enter your current software.";
      }
    }

    if (step === 2) {
      if (!formData.firstName.trim()) {
        errors.firstName = "First name is required.";
      }

      if (!formData.lastName.trim()) {
        errors.lastName = "Last name is required.";
      }

      if (!formData.clinicContactNumber.trim()) {
        errors.clinicContactNumber = "Clinic contact number is required.";
      } else if (!isValidPhoneNumber(formData.clinicContactNumber)) {
        errors.clinicContactNumber = "Please enter a valid phone number.";
      }

      if (!formData.email.trim()) {
        errors.email = "Email address is required.";
      } else if (!isValidEmail(formData.email)) {
        errors.email = "Please enter a valid email address.";
      }
    }

    if (step === 3) {
      const branchCount = Number(formData.numberOfBranches || 0);

      if (branchCount < 1) {
        errors.numberOfBranches = "Number of branches is required.";
      } else if (branchCount > MAX_BRANCHES) {
        errors.numberOfBranches = `Number of branches cannot exceed ${MAX_BRANCHES}.`;
      }

      if (!formData.businessType) {
        errors.businessType = "Business type is required.";
      }

      if (!formData.clinicName.trim()) {
        errors.clinicName = "Clinic name is required.";
      }

      if (!formData.clinicAddressLine1.trim()) {
        errors.clinicAddressLine1 = "Address line 1 is required.";
      }

      (formData.clinicBranches || []).forEach((branch, index) => {
        const hasAnyValue =
          branch.clinicName?.trim() ||
          branch.clinicAddressLine1?.trim() ||
          branch.clinicAddressLine2?.trim() ||
          branch.clinicContactNumber?.trim() ||
          branch.veterinarianContactNumber?.trim();

        if (!hasAnyValue) return;

        if (!branch.clinicName?.trim()) {
          errors[`clinicBranches.${index}.clinicName`] =
            "Branch clinic name is required.";
        }

        if (!branch.clinicAddressLine1?.trim()) {
          errors[`clinicBranches.${index}.clinicAddressLine1`] =
            "Branch address line 1 is required.";
        }

        if (!branch.clinicContactNumber?.trim()) {
          errors[`clinicBranches.${index}.clinicContactNumber`] =
            "Branch contact number is required.";
        } else if (!isValidPhoneNumber(branch.clinicContactNumber)) {
          errors[`clinicBranches.${index}.clinicContactNumber`] =
            "Please enter a valid branch contact number.";
        }
      });
    }

    if (step === 4) {
      if (!formData.preferredDate) {
        errors.preferredDate = "Please select your preferred demo date.";
      }

      if (!formData.preferredTime) {
        errors.preferredTime = "Please select your preferred demo time.";
      } else if (!isTimeWithinRange(formData.preferredTime, formData.preferredDate)) {
        errors.preferredTime = `Preferred time must be between ${getDynamicMinAllowedDisplay(
          formData.preferredDate
        )} and ${MAX_PREFERRED_TIME_DISPLAY} (PH/Manila time).`;
      }
    }

    return errors;
  };

  const handleNextStep = () => {
    if (currentStep >= 4) return;

    const errors = getStepValidationErrors(currentStep);

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      scrollToFirstError(errors);
      return;
    }

    setFieldErrors({});
    setCurrentStep((prev) => prev + 1);
  };

  const handlePreviousStep = () => {
    if (currentStep <= 1) return;
    setCurrentStep((prev) => prev - 1);
  };

  const labelClass =
    "mb-2 block text-sm sm:text-base font-bold tracking-wider text-slate-500 uppercase";
  const innerWrapperClass = "relative group flex w-full min-w-0 items-center";
  const inputClass =
    "w-full min-w-0 truncate rounded-xl border border-slate-200 bg-slate-50/50 py-2.5 pr-4 pl-12 text-base font-medium text-slate-900 placeholder:text-sm placeholder:text-slate-400 transition-all duration-300 focus:border-blue-600 focus:bg-white focus:ring-[4px] focus:ring-blue-600/10 focus:outline-none hover:border-slate-300 hover:bg-slate-50 sm:py-4 sm:text-lg sm:placeholder:text-base";
  const iconClass =
    "pointer-events-none absolute top-1/2 left-4 z-10 -translate-y-1/2 text-slate-400 transition-colors duration-300 group-hover:text-slate-500 group-focus-within:text-blue-600";

  const modalLayoutClass = inline
    ? "flex w-full flex-col bg-white md:flex-row"
    : "flex h-full w-full flex-col overflow-y-auto bg-white sm:rounded-[inherit] md:flex-row md:overflow-hidden";

  const modalContent = (
    <motion.div variants={modalReveal} initial="hidden" animate="show" className={modalLayoutClass}>
      {/* LEFT PANEL */}
      <motion.div
        variants={leftPanelReveal}
        className="relative hidden w-[42%] shrink-0 flex-col justify-between overflow-hidden bg-[#0A102E] p-6 text-white md:flex lg:p-7">
        <div className="pointer-events-none absolute -top-[10%] -left-[20%] h-125 w-125 rounded-full bg-blue-600/20 blur-[120px]" />
        <div className="pointer-events-none absolute -right-[20%] -bottom-[10%] h-100 w-100 rounded-full bg-indigo-600/20 blur-[100px]" />

        <div className="relative z-10">
          <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-blue-400 shadow-inner backdrop-blur-md">
            <Sparkles size={20} strokeWidth={1.5} />
          </div>
          <h1 className="mb-2 text-[1.9rem] leading-[1.12] font-extrabold tracking-tight lg:text-[2.25rem]">
            Modernize Your <br />
            <span className="bg-linear-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">
              Veterinary business
            </span>
          </h1>
          <p className="pr-2 text-sm leading-relaxed text-slate-100 sm:text-base">
            Experience how VetAssist streamlines workflows, saving your team time and improving
            patient care.
          </p>

          <div className="mt-6 space-y-4">
            <div className="flex items-start gap-4">
              <div className="mt-0.5 rounded-full bg-blue-500/10 p-2 text-blue-400">
                <Clock size={18} strokeWidth={2} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white lg:text-base">Save Hours Daily</h3>
                <p className="mt-1 text-sm text-slate-200">Automate scheduling and records.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="mt-0.5 rounded-full bg-indigo-500/10 p-2 text-indigo-400">
                <ShieldCheck size={18} strokeWidth={2} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white lg:text-base">Secure Data</h3>
                <p className="mt-1 text-sm text-slate-200">
                  Enterprise-grade security for your clinic.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="mt-0.5 rounded-full bg-emerald-500/10 p-2 text-emerald-400">
                <CheckCircle2 size={18} strokeWidth={2} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white lg:text-base">Easy Migration</h3>
                <p className="mt-1 text-sm text-slate-200">
                  We help you move from your old software.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10 mt-auto pt-4">
          <p className="mb-2 text-[10px] font-bold tracking-widest text-slate-300 uppercase lg:text-xs">
            Trusted & Certified
          </p>
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-28 items-center justify-center overflow-hidden rounded-xl border border-white/20 bg-white p-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.2)] backdrop-blur-md transition-all duration-300 lg:h-14 lg:w-32 lg:p-2">
              <img
                src={trustMarkBadge}
                alt="TrustMark"
                className="h-full w-full object-contain opacity-95 saturate-110"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="flex h-12 w-28 items-center justify-center overflow-hidden rounded-xl border border-white/20 bg-white p-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.2)] backdrop-blur-md transition-all duration-300 lg:h-14 lg:w-32 lg:p-2">
              <img
                src={dpoDpsMark}
                alt="DPO/DPS"
                className="h-full w-full object-contain opacity-95 saturate-110"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </motion.div>

      {/* RIGHT PANEL - Form Area */}
      <motion.div
        variants={rightPanelReveal}
        className="flex w-full min-w-0 flex-col overflow-hidden bg-white md:w-[58%]">
        <motion.div
          variants={formContainer}
          initial="hidden"
          animate="show"
          className="demo-modal-scroll relative flex-1 overflow-y-auto px-4 pt-6 pb-4 pr-5 sm:p-8 sm:pr-10 lg:p-12 lg:pr-14">
          <div className="mb-4 flex flex-col gap-2 sm:mb-6 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
            <h2 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
              Your Details
            </h2>
            <span className="inline-flex w-fit items-center gap-2 rounded-full bg-rose-50 px-3 py-1.5 text-xs font-bold tracking-wider text-rose-500 uppercase">
              <span className="h-2 w-2 animate-pulse rounded-full bg-rose-500"></span>
              Required Fields
            </span>
          </div>

          <div className="mb-4 flex gap-2 sm:mb-8">
            <div
              className={`h-2 flex-1 rounded-full transition-colors duration-500 ${currentStep >= 1 ? "bg-blue-600" : "bg-slate-200"}`}
            />
            <div
              className={`h-2 flex-1 rounded-full transition-colors duration-500 ${currentStep >= 2 ? "bg-blue-600" : "bg-slate-200"}`}
            />
            <div
              className={`h-2 flex-1 rounded-full transition-colors duration-500 ${currentStep >= 3 ? "bg-blue-600" : "bg-slate-200"}`}
            />
            <div
              className={`h-2 flex-1 rounded-full transition-colors duration-500 ${currentStep >= 4 ? "bg-blue-600" : "bg-slate-200"}`}
            />
          </div>

          <div className="mb-4 text-sm font-semibold text-blue-700 sm:mb-6">
            Step {currentStep} of 4
          </div>

          {formData.referralCode && (
            <div
              className={`mb-5 rounded-xl border px-4 py-3 text-sm font-semibold ${
                referralStatus.isChecking
                  ? "border-blue-100 bg-blue-50 text-blue-700"
                  : referralStatus.isValid
                    ? "border-emerald-100 bg-emerald-50 text-emerald-700"
                    : "border-rose-100 bg-rose-50 text-rose-600"
              }`}>
              {referralStatus.message || `Referral: ${formData.referralCode}`}
            </div>
          )}

          <form id="demo-form" onSubmit={handleSubmit} className="space-y-6 sm:space-y-12">
            {currentStep === 1 && (
              <motion.div variants={formItem} className="space-y-4 sm:space-y-6">
                <h3 className="border-b border-slate-100 pb-2 text-xl font-bold text-slate-800">
                  1. Software Details
                </h3>
                <div ref={setFieldRef("hasSoftware")} className="w-full">
                  <label className={labelClass}>
                    Current Software Status <span className="text-rose-500">*</span>
                  </label>
                  <div className="group relative w-full min-w-0">
                    <Laptop size={20} className={iconClass} />
                    <Select
                      value={formData.hasSoftware}
                      onValueChange={(value) => {
                        clearFieldError("hasSoftware", "currentSoftwareName");

                        setFormData((prev) => ({
                          ...prev,
                          hasSoftware: value,
                          currentSoftwareName: value === "yes" ? prev.currentSoftwareName : "",
                        }));
                      }}>
                      <SelectTrigger className={`h-11 w-full rounded-xl border border-slate-200 bg-slate-50/50 pr-4 pl-12 text-left text-base font-medium text-slate-900 shadow-none transition-all duration-300 hover:border-slate-300 hover:bg-slate-50 focus-visible:ring-4 focus-visible:ring-blue-600/10 sm:h-14 sm:py-8 sm:text-lg${errorClass("hasSoftware")}`}>
                        <SelectValue placeholder="Select your current status..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes" className="py-2 text-base sm:py-2 sm:text-lg">
                          Yes, we currently use a software
                        </SelectItem>
                        <SelectItem value="no" className="py-2 text-base sm:py-2 sm:text-lg">
                          No, we are not using any software
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {errorText("hasSoftware")}
                  </div>
                </div>

                <AnimatePresence mode="popLayout">
                  {formData.hasSoftware === "yes" && (
                    <motion.div
                      ref={setFieldRef("currentSoftwareName")}
                      initial={{ opacity: 0, height: 0, marginTop: 0 }}
                      animate={{ opacity: 1, height: "auto", marginTop: 24 }}
                      exit={{ opacity: 0, height: 0, marginTop: 0 }}
                      className="w-full overflow-hidden">
                      <label className={labelClass}>
                        What software are you using? <span className="text-rose-500">*</span>
                      </label>
                      <div className={innerWrapperClass}>
                        <Search size={20} className={iconClass} />
                        <input
                          type="text"
                          name="currentSoftwareName"
                          value={formData.currentSoftwareName}
                          onChange={handleInputChange}
                          placeholder="e.g., Hippo Manager, Neo"
                          required={formData.hasSoftware === "yes"}
                          className={`${inputClass}${errorClass("currentSoftwareName")}`}
                        />
                      </div>
                      {errorText("currentSoftwareName")}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}

            {currentStep === 2 && (
              <motion.div variants={formItem} className="space-y-4 sm:space-y-6">
                <h3 className="border-b border-slate-100 pb-2 text-xl font-bold text-slate-800">
                  2. Personal Information
                </h3>
                <div className="grid gap-x-4 gap-y-4 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-8">
                  <div ref={setFieldRef("firstName")} className="w-full">
                    <label className={labelClass}>
                      First Name <span className="text-rose-500">*</span>
                    </label>
                    <div className={innerWrapperClass}>
                      <User size={20} className={iconClass} />
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        placeholder="Jane"
                        required
                        className={`${inputClass}${errorClass("firstName")}`}
                      />
                    </div>
                    {errorText("firstName")}
                  </div>

                  <div ref={setFieldRef("lastName")} className="w-full">
                    <label className={labelClass}>
                      Last Name <span className="text-rose-500">*</span>
                    </label>
                    <div className={innerWrapperClass}>
                      <User size={20} className={iconClass} />
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        placeholder="Doe"
                        required
                        className={`${inputClass}${errorClass("lastName")}`}
                      />
                    </div>
                    {errorText("lastName")}
                  </div>

                  <div ref={setFieldRef("clinicContactNumber")} className="w-full">
                    <label className={labelClass}>
                      Clinic Contact Number <span className="text-rose-500">*</span>
                    </label>
                    <div className={innerWrapperClass}>
                      <Phone size={20} className={iconClass} />
                      <input
                        type="tel"
                        name="clinicContactNumber"
                        value={formData.clinicContactNumber}
                        onChange={handleInputChange}
                        placeholder="+639123456789"
                        maxLength={13}
                        required
                        className={`${inputClass}${errorClass("clinicContactNumber")}`}
                      />
                    </div>
                    {errorText("clinicContactNumber")}
                  </div>

                  <div ref={setFieldRef("veterinarianContactNumber")} className="w-full">
                    <label className={labelClass}>Veterinarian Contact Number</label>
                    <div className={innerWrapperClass}>
                      <Phone size={20} className={iconClass} />
                      <input
                        type="tel"
                        name="veterinarianContactNumber"
                        value={formData.veterinarianContactNumber}
                        onChange={handleInputChange}
                        placeholder="+639123456789"
                        maxLength={13}
                        className={`${inputClass}${errorClass("veterinarianContactNumber")}`}
                      />
                    </div>
                    {errorText("veterinarianContactNumber")}
                  </div>

                  <div ref={setFieldRef("email")} className="w-full sm:col-span-2">
                    <label className={labelClass}>
                      Email Address <span className="text-rose-500">*</span>
                    </label>
                    <div className={innerWrapperClass}>
                      <Mail size={20} className={iconClass} />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="jane@clinic.com"
                        required
                        className={`${inputClass}${errorClass("email")}`}
                      />
                    </div>
                    {errorText("email")}
                  </div>
                </div>
              </motion.div>
            )}

            {currentStep === 3 && (
              <motion.div variants={formItem} className="space-y-4 sm:space-y-6">
                <h3 className="border-b border-slate-100 pb-2 text-xl font-bold text-slate-800">
                  3. Clinic Information
                </h3>

                <div ref={setFieldRef("numberOfBranches")} className="w-full">
                  <label className={labelClass}>
                    Number of Branches <span className="text-rose-500">*</span>
                  </label>
                  <div className={innerWrapperClass}>
                    <Building2 size={20} className={iconClass} />
                      <input
                        type="number"
                        name="numberOfBranches"
                        value={formData.numberOfBranches}
                        onChange={(e) => handleNumberOfBranchesChange(e.target.value)}
                        min="1"
                        max="20"
                        step="1"
                        placeholder="1"
                        className={`${inputClass}${errorClass("numberOfBranches")}`}
                      />
                  </div>
                  {errorText("numberOfBranches")}
                </div>

                <div ref={setFieldRef("businessType")} className="w-full">
                  <label className={labelClass}>
                    Business Type <span className="text-rose-500">*</span>
                  </label>
                  <div className="group relative w-full min-w-0">
                    <Building2 size={20} className={iconClass} />
                   <Select
                        value={formData.businessType}
                        onValueChange={(value) => {
                        clearFieldError("businessType");
                        setFormData((prev) => ({ ...prev, businessType: value }));
                      }}>
                      <SelectTrigger className={`h-11 w-full rounded-xl border border-slate-200 bg-slate-50/50 pr-4 pl-12 text-left text-base font-medium text-slate-900 shadow-none transition-all duration-300 hover:border-slate-300 hover:bg-slate-50 focus-visible:ring-4 focus-visible:ring-blue-600/10 sm:h-14 sm:py-8 sm:text-lg${errorClass("businessType")}`}>
                        <SelectValue placeholder="Select business type..." />
                      </SelectTrigger>
                      <SelectContent>

                        <SelectItem
                          value="Veterinary Clinic"
                          className="py-2 text-base sm:py-2 sm:text-lg">
                          Veterinary Clinic
                        </SelectItem>

                        <SelectItem
                          value="Animal Hospital"
                          className="py-2 text-base sm:py-2 sm:text-lg">
                          Animal Hospital
                        </SelectItem>

                        <SelectItem
                          value="Mobile Vet"
                          className="py-2 text-base sm:py-2 sm:text-lg">
                          Mobile Vet
                        </SelectItem>

                        <SelectItem
                          value="Grooming and Hotel"
                          className="py-2 text-base sm:py-2 sm:text-lg">
                          Grooming and Hotel
                        </SelectItem>

                        <SelectItem value="Clinic and Pet Shop" className="py-2 text-base sm:py-2 sm:text-lg">
                          Clinic and Pet Shop
                        </SelectItem>

                         <SelectItem value="Multi-Branch" className="py-2 text-base sm:py-2 sm:text-lg">
                          Multi-Branch
                        </SelectItem>           

                        <SelectItem value="Other" className="py-2 text-base sm:py-2 sm:text-lg">
                          Other
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {errorText("businessType")}
                </div>

                <div className="space-y-4 sm:space-y-6">
                  <p className="text-sm font-semibold tracking-wider text-slate-500 uppercase">
                    Primary Clinic
                  </p>
                  <div className="grid gap-x-4 gap-y-4 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-6">
                    <div ref={setFieldRef("clinicName")} className="w-full sm:col-span-2">
                      <label className={labelClass}>
                        Clinic Name <span className="text-rose-500">*</span>
                      </label>
                      <div className={innerWrapperClass}>
                        <Building2 size={20} className={iconClass} />
                        <input
                          type="text"
                          name="clinicName"
                          value={formData.clinicName}
                          onChange={handleInputChange}
                          placeholder="Happy Paws Vet Clinic"
                          required
                          className={`${inputClass}${errorClass("clinicName")}`}
                        />
                      </div>
                      {errorText("clinicName")}
                    </div>

                    <div ref={setFieldRef("clinicAddressLine1")} className="w-full sm:col-span-2">
                      <label className={labelClass}>
                        Address Line 1 <span className="text-rose-500">*</span>
                      </label>
                      <div className={innerWrapperClass}>
                        <MapPin size={20} className={iconClass} />
                        <input
                          type="text"
                          name="clinicAddressLine1"
                          value={formData.clinicAddressLine1}
                          onChange={handleInputChange}
                          placeholder="Unit Number, Street, Building Name"
                          required
                          className={`${inputClass}${errorClass("clinicAddressLine1")}`}
                        />
                      </div>
                      {errorText("clinicAddressLine1")}
                    </div>

                    <div className="w-full sm:col-span-2">
                      <label className={labelClass}>Address Line 2</label>
                      <div className={innerWrapperClass}>
                        <MapPin size={20} className={iconClass} />
                        <input
                          type="text"
                          name="clinicAddressLine2"
                          value={formData.clinicAddressLine2}
                          onChange={handleInputChange}
                          placeholder="Suite, floor, landmark, or other optional details"
                          className={inputClass}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 rounded-xl border border-slate-200 bg-slate-50/50 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold tracking-wider text-slate-500 uppercase">
                      Other Branch Details{" "}
                      <span className="font-normal text-slate-400 normal-case">(Optional)</span>
                    </p>

                    <button
                      type="button"
                      onClick={handleAddClinicBranch}
                      disabled={
                        Number(formData.numberOfBranches || 0) <= 0 ||
                        (formData.clinicBranches || []).length >= Number(formData.numberOfBranches || 0)
                      }
                      className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Add Branch
                    </button>
                  </div>

                  {(formData.clinicBranches || []).map((branch, index) => (
                    <div
                      key={index}
                      className="space-y-4 rounded-xl border border-slate-200 bg-white/70 p-4"
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold tracking-wider text-slate-500 uppercase">
                          Branch Detail {index + 1}
                        </p>

                        <button
                          type="button"
                          onClick={() => handleRemoveClinicBranch(index)}
                          className="text-sm font-bold text-rose-500 hover:text-rose-700"
                        >
                          Remove
                        </button>
                      </div>

                      <div ref={setFieldRef(`clinicBranches.${index}.clinicName`)} className="w-full">
                        <label className={labelClass}>Clinic Name</label>
                        <div className={innerWrapperClass}>
                          <Building2 size={20} className={iconClass} />
                          <input
                            type="text"
                            value={branch.clinicName}
                            onChange={(e) =>
                              handleClinicBranchChange(index, "clinicName", e.target.value)
                            }
                            placeholder="Branch clinic name"
                            className={`${inputClass}${errorClass(`clinicBranches.${index}.clinicName`)}`}
                          />
                        </div>
                        {errorText(`clinicBranches.${index}.clinicName`)}
                      </div>

                      <div ref={setFieldRef(`clinicBranches.${index}.clinicAddressLine1`)} className="w-full">
                        <label className={labelClass}>Address Line 1</label>
                        <div className={innerWrapperClass}>
                          <MapPin size={20} className={iconClass} />
                          <input
                            type="text"
                            value={branch.clinicAddressLine1}
                            onChange={(e) =>
                              handleClinicBranchChange(index, "clinicAddressLine1", e.target.value)
                            }
                            placeholder="Unit Number, Street, Building Name"
                            className={`${inputClass}${errorClass(`clinicBranches.${index}.clinicAddressLine1`)}`}
                          />
                        </div>
                        {errorText(`clinicBranches.${index}.clinicAddressLine1`)}
                      </div>

                      <div ref={setFieldRef(`clinicBranches.${index}.clinicAddressLine2`)} className="w-full">
                        <label className={labelClass}>Address Line 2</label>
                        <div className={innerWrapperClass}>
                          <MapPin size={20} className={iconClass} />
                          <input
                            type="text"
                            value={branch.clinicAddressLine2}
                            onChange={(e) =>
                              handleClinicBranchChange(index, "clinicAddressLine2", e.target.value)
                            }
                            placeholder="Suite, floor, landmark, or optional details"
                            className={`${inputClass}${errorClass(`clinicBranches.${index}.clinicAddressLine2`)}`}
                          />
                        </div>
                        {errorText(`clinicBranches.${index}.clinicAddressLine2`)}
                      </div>

                      <div ref={setFieldRef(`clinicBranches.${index}.clinicContactNumber`)} className="w-full">
                        <label className={labelClass}>Contact Number</label>
                        <div className={innerWrapperClass}>
                          <Phone size={20} className={iconClass} />
                          <input
                            type="tel"
                            value={branch.clinicContactNumber}
                            onChange={(e) =>
                              handleClinicBranchChange(index, "clinicContactNumber", e.target.value)
                            }
                            placeholder="+639123456789"
                            maxLength={13}
                            className={`${inputClass}${errorClass(`clinicBranches.${index}.clinicContactNumber`)}`}
                          />
                        </div>
                        {errorText(`clinicBranches.${index}.clinicContactNumber`)}
                      </div>

                      <div ref={setFieldRef(`clinicBranches.${index}.veterinarianContactNumber`)} className="w-full">
                        <label className={labelClass}>Veterinarian Number</label>
                        <div className={innerWrapperClass}>
                          <Phone size={20} className={iconClass} />
                          <input
                            type="tel"
                            value={branch.veterinarianContactNumber}
                            onChange={(e) =>
                              handleClinicBranchChange(index, "veterinarianContactNumber", e.target.value)
                            }
                            placeholder="+639123456789"
                            maxLength={13}
                            className={`${inputClass}${errorClass(`clinicBranches.${index}.veterinarianContactNumber`)}`}
                          />
                        </div>
                        {errorText(`clinicBranches.${index}.veterinarianContactNumber`)}  
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {currentStep === 4 && (
              <motion.div variants={formItem} className="space-y-4 sm:space-y-6">
                <h3 className="border-b border-slate-100 pb-2 text-xl font-bold text-slate-800">
                  4. Preferred Demo Schedule
                </h3>
                <div className="space-y-3">
                <div
                  ref={(node) => {
                    if (node) {
                      fieldRefs.current.preferredDate = node;
                      fieldRefs.current.preferredTime = node;
                    }
                  }}
                  className={`rounded-xl transition-all ${
                    fieldErrors.preferredDate || fieldErrors.preferredTime
                      ? "ring-2 ring-rose-500 ring-offset-2"
                      : ""
                  }`}
                >
                  <TimePicker
                    value={formData.preferredTime}
                    onChange={handlePreferredTimeChange}
                    selectedDate={formData.preferredDate}
                    onDateChange={handlePreferredDateChange}
                    minTime={MIN_PREFERRED_TIME_DISPLAY}
                    maxTime={MAX_PREFERRED_TIME_DISPLAY}
                    minDate={startOfToday}
                    startMonth={calendarStartMonth}
                    endMonth={calendarEndMonth}
                  />
                </div>
                  {fieldErrors.preferredDate || fieldErrors.preferredTime || timeValidationError ? (
                    <p className="text-xs text-rose-500">
                      {fieldErrors.preferredDate || fieldErrors.preferredTime || timeValidationError}
                    </p>
                  ) : (
                    <p className="text-xs text-slate-500">
                      Earliest available booking is 1 hour from the current PH/Manila time, within{" "}
                      {MIN_PREFERRED_TIME_DISPLAY} to {MAX_PREFERRED_TIME_DISPLAY}.
                    </p>
                  )}
                </div>

                {isTurnstileEnabled && (
                  <div className="mt-4 flex flex-col items-start gap-3">
                    <TurnstileWidget
                      ref={turnstileRef}
                      className="w-full max-w-[320px]"
                      action="demo_request"
                      mode="managed"
                      requireManualInteraction
                      resetKey={turnstileResetKey}
                      onTokenChange={setTurnstileToken}
                      onError={(message) => toast.error(message)}
                    />
                  </div>
                )}
              </motion.div>
            )}
          </form>
        </motion.div>

        <div className="shrink-0 border-t border-slate-100 bg-white px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:p-6 lg:px-12 lg:py-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-end">
            <button
              type="button"
              onClick={handleClearForm}
              disabled={isPending}
              className="w-full cursor-pointer rounded-xl px-6 py-3 text-center text-base font-bold text-slate-400 transition-colors hover:text-slate-900 disabled:opacity-50 sm:w-auto sm:py-4">
              Clear
            </button>
            {currentStep > 1 && (
              <button
                type="button"
                onClick={handlePreviousStep}
                className="w-full cursor-pointer rounded-xl border border-slate-200 px-8 py-3 text-base font-bold text-slate-600 transition-all duration-300 hover:border-slate-300 hover:text-slate-900 sm:w-auto sm:py-4">
                Back
              </button>
            )}

            {currentStep < 4 ? (
              <button
                type="button"
                onClick={handleNextStep}
                className="group flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-[#1D4ED8] px-8 py-3 text-base font-bold text-white shadow-[0_8px_20px_-6px_rgba(29,78,216,0.5)] transition-all duration-300 hover:bg-[#1E40AF] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto sm:py-4">
                Next Step
                <ArrowRight
                  size={18}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </button>
            ) : (
              <button
                type="submit"
                form="demo-form"
                disabled={isPending}
                className="group flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-[#1D4ED8] px-8 py-3 text-base font-bold text-white shadow-[0_8px_20px_-6px_rgba(29,78,216,0.5)] transition-all duration-300 hover:bg-[#1E40AF] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto sm:py-4">
                {isPending ? "Submitting..." : "Submit Request"}
                {isPending ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <ArrowRight
                    size={18}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                )}
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );

  if (inline) {
    return (
      <div className="mx-auto w-full max-w-7xl px-4 pt-32 pb-16 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_40px_100px_-20px_rgba(0,0,0,0.22)]">
          {modalContent}
        </div>
      </div>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}

        <DialogContent
          style={{ perspective: "1500px" }}
          className="h-[calc(100dvh-1rem)] max-h-[calc(100dvh-1rem)] w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] overflow-hidden border-none bg-transparent p-0 shadow-[0_40px_100px_-20px_rgba(0,0,0,0.4)] data-[state=closed]:animate-none data-[state=open]:animate-none sm:h-[90vh] sm:max-h-[90vh] sm:w-full sm:max-w-[calc(100vw-2rem)] sm:rounded-[2rem] md:max-w-5xl">
          {modalContent}
        </DialogContent>
      </Dialog>
    </>
  );
}
